import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { action, computed, makeObservable, observable } from 'mobx';
import { useEffect } from 'react';

/**
 * Canonical list of modal names used across the app.
 * Keep one name per line, with documentation above each entry.
 */
export type ModalName =
  // Library create modal - creates a new library from /libraries
  | 'library-create'
  // Library delete modal - permanently deletes a library from /libraries
  | 'library-delete'
  // Library rename modal - modifies library name/thumbnail/glossary from /libraries
  | 'library-rename'
  // Mobile edit-library sheet - the mobile equivalent of library-rename (name/thumbnail/glossary)
  | 'mobile-library-edit'
  // Add media modal - adds media to a library/folder via URL import or file upload
  | 'add-media'
  // Edit nickname modal - sets/changes the display name from the workspace greeting
  | 'edit-nickname'
  // Media delete modal - deletes one or multiple media items from library view
  | 'media-delete'
  // Media rename modal - renames a single media item from library view
  | 'media-rename'
  // Folder edit modal - creates a library folder, or renames / recolours one, from library view
  | 'folder-edit'
  // Folder picker modal - picks the folder to move media or a folder into, from library view
  | 'folder-picker'
  // Folder delete modal - deletes a library folder with everything in it, from library view
  | 'folder-delete'
  // Failed-media modal - shows failed uploads/indexing. Registered by the shared
  // FailedMediaListModal shell, so it covers all five of its surfaces (library, upload results,
  // notification-bell details, rich event card, batch-card skipped duplicates)
  | 'failed-media'
  // Moment modal - shows a single moment/video with transcript/visual summary
  | 'moment'
  // Moment merge modal - merges multiple moments into a single compilation
  | 'moment-merge'
  // Moment edit (trim) modal - edit/trim a single moment from within the moment merge flow
  | 'moment-edit'
  // Choose media modal - pick external media (URLs) to add to a library
  | 'choose-media'
  // Choose media modal - summary/confirmation before adding external media
  | 'choose-media-summary'
  // Choose file upload modal - pick local files to upload into a library
  | 'choose-file-upload'
  // Choose file upload modal - summary/confirmation before uploading local files
  | 'choose-file-upload-summary'
  // URL validation modal - review and fix problematic URLs before adding media
  | 'url-validation'
  // Change subscription confirmation modal - confirm subscription plan changes
  | 'change-subscription'
  // Chat rename modal - renames a chat from the nav recent-chats list
  | 'chat-rename'
  // Chat delete modal - deletes a chat from the nav recent-chats list
  | 'chat-delete'
  // Connect social account modal - picks the platform to connect, from Settings, the schedule and the compose page
  | 'connect-social-account'
  // Schedule time zone dialog - picks the zone the schedule and the compose picker show times in
  | 'schedule-time-zone';

/**
 * Lightweight global registry for "is any modal open?" logic, so that other parts of the app
 * (the drag-drop overlay, Esc handling, keyboard shortcuts) can defer while one is up.
 *
 * Modals do not talk to this store directly — they call `useModalRegistration(id, isVisible)`,
 * which is the only supported entry point. `registerModal`/`unregisterModal` are its
 * implementation; a call to either from outside this file is a bug, and
 * `grep -rn "registerModal\\|unregisterModal" src --include=*.tsx` returning nothing is the
 * invariant that keeps this store honest.
 *
 * Why that matters: everything here derives from `modalStack`, so a single unbalanced
 * registration pins the stack non-empty for the rest of the session — and every consumer then
 * fails silently rather than loudly.
 */
export class ModalStore extends ReactionManager {
  /**
   * Internal map of modal IDs to reference counts.
   * A reference count lets the same modal ID register multiple times
   * safely (e.g. nested flows) without leaking state.
   */
  @observable
  private openModalCounts = new Map<string, number>();

  /**
   * Stack of modal IDs reflecting z-order (last = top-most).
   * Each registration pushes an ID; unregistration removes the latest occurrence.
   */
  @observable
  private modalStack: string[] = [];

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get hasOpenModals(): boolean {
    return this.modalStack.length > 0;
  }

  @computed
  get openModalIds(): string[] {
    // Preserve order by last appearance in the stack (bottom → top).
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const id of this.modalStack) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    return ids;
  }

  /**
   * ID of the top-most (last opened) modal, or null if none are open.
   */
  @computed
  get topModalId(): string | null {
    if (this.modalStack.length === 0) return null;
    return this.modalStack[this.modalStack.length - 1] ?? null;
  }

  /**
   * Modals that allow drag-drop overlay to show on top.
   * Configurable list of modal IDs where file dragging is permitted.
   */
  private dragDropAllowedModals = new Set<ModalName>([
    'choose-file-upload',
    'add-media',
    // Note that we are not adding choose-file-upload-summary to the allowed list because you shouldn't be able to add new files during the summary!
  ]);

  private dragDropOverlayShouldBeTransparent = new Set<ModalName>(['add-media']);

  /**
   * True when the drag-drop overlay and drop handling are allowed: nothing is open, or
   * every open modal permits it.
   *
   * Stack-wide rather than top-only, which matters now that the file picker registers.
   * `choose-file-upload` legitimately opens on top of `add-media`, and conversely a modal
   * that must block drops (`choose-file-upload-summary` — you shouldn't add files during
   * the summary) has to keep blocking even when something else is top-most. Asking about
   * `topModalId` alone gets both of those wrong depending on which one happened to open last.
   *
   * `every` on an empty list is `true`, so the "no modals open" case needs no branch.
   */
  @computed
  get allowDragDropOverlay(): boolean {
    return this.openModalIds.every((id) => this.dragDropAllowedModals.has(id as ModalName));
  }

  /**
   * Transparent when ANY open modal wants it — such a modal renders its own drag cue, so a
   * second opaque one on top would hide it. `some`, for the mirror of the reason
   * `allowDragDropOverlay` uses `every`.
   */
  @computed
  get shouldDragDropOverlayBeTransparent(): boolean {
    return this.openModalIds.some((id) =>
      this.dragDropOverlayShouldBeTransparent.has(id as ModalName),
    );
  }

  /**
   * Raw stack (bottom → top), duplicates included — for the debug tab only.
   *
   * Every consumer of this store fails SILENTLY when the stack is wrong: drag-drop stops
   * working, Esc stops walking back, a keyboard shortcut stops firing. Surfacing the stack
   * turns the next unbalanced registration into something visible in five seconds instead of
   * a bug report about drag-drop "randomly" breaking.
   */
  @computed
  get debugModalStack(): readonly string[] {
    return this.modalStack;
  }

  /**
   * Low-level API: explicitly register an open modal.
   */
  @action
  registerModal(id: ModalName): void {
    const current = this.openModalCounts.get(id) ?? 0;
    this.openModalCounts.set(id, current + 1);
    this.modalStack.push(id);
  }

  /**
   * Low-level API: explicitly unregister / close a modal.
   */
  @action
  unregisterModal(id: ModalName): void {
    const current = this.openModalCounts.get(id);
    if (current === undefined) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[ModalStore] Attempted to unregister non-existent modal: ${id}`);
      }
      return;
    }

    if (current <= 1) {
      this.openModalCounts.delete(id);
    } else {
      this.openModalCounts.set(id, current - 1);
    }

    // Remove the latest occurrence of this ID from the stack.
    const idx = this.modalStack.lastIndexOf(id);
    if (idx !== -1) {
      this.modalStack.splice(idx, 1);
    } else if (process.env.NODE_ENV !== 'production') {
      console.warn(`[ModalStore] Modal ${id} not found in stack during unregister`);
    }
  }

  override deinit(): void {
    this.openModalCounts.clear();
    this.modalStack = [];
    super.deinit();
  }
}

export const { Provider: ModalProvider, useStore: useModalStore } = createContext(ModalStore, {
  dispose: (store) => store.deinit(),
});

/**
 * Declarative modal registration: call it with "am I visible right now?" and the store stays
 * balanced by construction, because the registration and its release live in the same effect run.
 * An unmount-while-open therefore releases correctly.
 *
 * Refcount-safe. Several components may hold the same id at once — `library-create` is deliberately
 * shared by the desktop modal, the mobile drawer and `NewLibraryModalBase` — and the id stays open
 * until the last of them closes.
 *
 * Pass the truest "am I visible" expression you have, not just an `open` flag: a modal that renders
 * `null` until its data arrives is not visible yet (see `LibraryRenameModal`, which passes
 * `open && !!library`).
 *
 * NEVER register from `onOpen`/`onClose` handlers. A host that unmounts the modal while it is open —
 * `DeleteBatchModalHost` does exactly that, by removing the notification that keeps it mounted —
 * leaks a stack entry. Every consumer reads the stack, so that pins `hasOpenModals` true and
 * silently disables drag-drop for the rest of the session.
 *
 * Usage:
 *   useModalRegistration('choose-file-upload', open);
 */
export const useModalRegistration = (id: ModalName, isOpen: boolean): void => {
  const modalStore = useModalStore();

  useEffect(() => {
    if (!isOpen) return;
    modalStore.registerModal(id);
    return () => modalStore.unregisterModal(id);
  }, [id, isOpen, modalStore]);
};
